/**
 * Session migration runner.
 *
 * Composite version key = `APP_VERSION_BASE + BUILD_HASH`.
 *   - APP_VERSION_BASE is manually bumped for deliberate schema breaks.
 *   - BUILD_HASH is auto-injected from `git rev-parse --short HEAD` via
 *     `scripts/inject-build-hash.js` (runs on prestart/prebuild).
 * So every production deploy automatically invalidates stale sessions,
 * AND we can still force an immediate cleanup by bumping APP_VERSION_BASE.
 *
 * Known session keys are wiped on bump; preference keys are preserved.
 *
 * History:
 *   - 2026-06-29.1: Wipe Ultra Admin impersonation residue across sessions.
 *   - 2026-06-29.2: Deploy-hash auto-bump.
 */

import { BUILD_HASH } from './buildInfo';

const APP_VERSION_BASE = '2026-06-29.2';
const APP_VERSION = `${APP_VERSION_BASE}-${BUILD_HASH || 'unknown'}`;

const SESSION_KEYS = [
  'token',
  'impersonation',
  'ultra_admin_token',
  'ultra_admin_user',
  'user',
];

// Keys that should NEVER be cleared by this migration (user preferences).
// Add more here when needed.
const PRESERVE_KEYS = [
  'theme',
  'language',
  'dismissed_banners',
];

export function runSessionMigration() {
  try {
    const stored = localStorage.getItem('app_version');
    if (stored === APP_VERSION) return; // nothing to do

    // First-time visitor: just stamp the version, no cleanup needed.
    if (!stored) {
      localStorage.setItem('app_version', APP_VERSION);
      return;
    }

    // Snapshot user preferences so we restore them after cleanup.
    const preserved = {};
    PRESERVE_KEYS.forEach((k) => {
      const v = localStorage.getItem(k);
      if (v !== null) preserved[k] = v;
    });

    // Wipe known session keys (NOT preferences, NOT arbitrary keys).
    SESSION_KEYS.forEach((k) => localStorage.removeItem(k));

    // Restore preferences (defensive — they weren't in SESSION_KEYS anyway).
    Object.entries(preserved).forEach(([k, v]) => localStorage.setItem(k, v));

    // Stamp the new version so we don't repeat the cleanup next reload.
    localStorage.setItem('app_version', APP_VERSION);

    // eslint-disable-next-line no-console
    console.log(`🔄 Session migration done: ${stored} → ${APP_VERSION}`);
  } catch (err) {
    // localStorage can throw in private mode / quota; never break boot.
    // eslint-disable-next-line no-console
    console.warn('Session migration failed (non-fatal):', err);
  }
}

// Exposed for debug / tests.
export const APP_VERSION_FOR_TEST = APP_VERSION;
